import { useTranslation } from "@i18n";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { Link } from "react-router-dom";
import { getNotificationsCount, setShowNotification } from "../../actions/notification";
import AllNotification from "./AllNotification";
import ActionCards from "./ActionCard";
import { RootState } from "@Reducers/index";
import ExportedFiles from "./exportedFiles";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import { formCategories } from "@Helpers";
import type { GetAllExportDataResponse } from "@ServerTypes/schedule/schedule.schema";
import type { GetActionableCountResponse } from "@ServerTypes/projects/project.schema";
import type { CustomModuleItem } from "@ServerTypes/portal/portal.schema";

function Notification() {
  const { t } = useTranslation();
  const showNotification = useSelector((state: RootState) => state.showNotification);
  const unreadNotificationsCount = useSelector((state: RootState) => state.unreadNotificationsCount);
  const customModules = useSelector((state: RootState) => state.customModules);
  const layout = useSelector((state: RootState) => state.layout);
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("allNotifications");
  const notificationStatus = useRef(showNotification);
  const [takeActionCount, setTakeActionCount] = useState(0);
  const [exportedFilesCount, setExportedFilesCount] = useState(0);
  const [notificationCountLoading, setNotificationCountLoading] = useState(true);
  const [takeActionCountLoading, setTakeActionCountLoading] = useState(true);
  const [exportedFilesCountLoading, setExportedFilesCountLoading] = useState(true);

  const handleTakeActionCountChange = useCallback((count: number) => {
    setTakeActionCount((prev) => Math.max(prev, count));
  }, []);

  const handleExportedFilesCountChange = useCallback((count: number) => {
    setExportedFilesCount((prev) => Math.max(prev, count));
  }, []);

  const mouseDownInsideRef = useRef(false);
  const handleMouseDown = (e: MouseEvent) => {
    // The listener is on `window`, so the target is any `EventTarget`; only `Element` has `closest`.
    mouseDownInsideRef.current = e.target instanceof Element && !!e.target.closest(".preventNotificationClose");
  };
  const closeNotificationModal = useCallback(() => {
    dispatch(setShowNotification(false));
  }, []);

  const handleClick = useCallback(() => {
    if (!mouseDownInsideRef.current && notificationStatus.current) {
      closeNotificationModal();
    }
  }, [closeNotificationModal]);

  const getTabCount = (tabId: string) => {
    switch (tabId) {
      case "allNotifications":
        // The store slice is a plain number, so the object branch is already dead code; the
        // assertion preserves it verbatim rather than deleting it.
        return typeof unreadNotificationsCount === "number"
          ? unreadNotificationsCount
          : (unreadNotificationsCount as { count?: number } | undefined)?.count || 0;
      case "takeAction":
        return takeActionCount;
      case "exportedFiles":
        return exportedFilesCount;
      default:
        return 0;
    }
  };

  const isTabCountLoading = (tabId: string) => {
    switch (tabId) {
      case "allNotifications":
        return notificationCountLoading;
      case "takeAction":
        return takeActionCountLoading;
      case "exportedFiles":
        return exportedFilesCountLoading;
      default:
        return false;
    }
  };

  const allTabs = [
    {
      name: t("Notifications"),
      id: "allNotifications",
      redirectUrl: "/notifications?section=allNotifications",
    },
    {
      name: t("Take Action"),
      id: "takeAction",
      redirectUrl: "/notifications?section=takeAction&module=reviews",
    },
    {
      name: t("Exported Files"),
      id: "exportedFiles",
      redirectUrl: "/notifications?section=exportedFiles",
    },
  ];

  useEffect(() => {
    notificationStatus.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("click", handleClick);
    };
  }, [handleClick]);

  useEffect(() => {
    const fetchNotificationsCount = async () => {
      try {
        await dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
      } finally {
        setNotificationCountLoading(false);
      }
    };

    // Fire-and-forget: an effect callback cannot be async; the finally clears the loader.
    void fetchNotificationsCount();
  }, [dispatch]);

  useEffect(() => {
    const fetchExportedFilesCount = async () => {
      try {
        setExportedFilesCountLoading(true);
        const response = await axiosWrapper.post<GetAllExportDataResponse>(
          `${getBackendUrl()}/schedule/getAllExportData`,
          {
            value: { skip: 0, take: 0 },
          },
        );
        const count = response.data.status ? response.data.count : 0;
        setExportedFilesCount(count);
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        setExportedFilesCountLoading(false);
      }
    };

    const fetchTakeActionCount = async () => {
      try {
        setTakeActionCountLoading(true);
        const response = await axiosWrapper.post<GetActionableCountResponse>(
          `${getBackendUrl()}/projects/getActionableCount`,
        );
        if (response.data.status) {
          const moduleCount = response.data.data!;
          let total = 0;
          if (layout.REVIEWFLOW?.ENABLE) {
            total += moduleCount.reviews?.count || 0;
          }
          if (layout.ISSUES?.ENABLE) {
            total += moduleCount.issues?.count || 0;
          }
          if (layout.INSPECTION?.ENABLE) {
            total += moduleCount[formCategories.inspection]?.count || 0;
          }
          if (layout.PROGRESS_UPDATES?.ENABLE) {
            total += moduleCount[formCategories.progressUpdates]?.count || 0;
          }
          customModules.forEach((module: CustomModuleItem) => {
            total += moduleCount[module.resourceType!]?.count || 0;
          });
          setTakeActionCount(total);
        }
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        setTakeActionCountLoading(false);
      }
    };

    if (showNotification) {
      // Fire-and-forget, and deliberately left in parallel: the two counts are independent and
      // each clears its own loader.
      void fetchExportedFilesCount();
      void fetchTakeActionCount();
    }
  }, [showNotification, layout, customModules]);

  return (
    <>
      {showNotification && (
        <div className="shadow-[0_2.5rem_2.5rem_rgba(106,168,177,0.2)] top-14 inset-e-28 bg-white absolute max-h-[80vh] w-2/5 z-30 border border-[#ecf3fe] overflow-hidden rounded-2xl preventNotificationClose flex flex-col">
          {/* Header + Tab Section */}
          <div className="sticky top-0 z-10 bg-white">
            <div className="px-6 pt-4 pb-2 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">{t("My Dashboard")}</h2>
              <Link
                to={allTabs.find((tab) => tab.id === activeTab)?.redirectUrl || "/notifications"}
                onClick={closeNotificationModal}
              >
                <IconComponent name="Redirect" width="1rem" height="1rem" className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex border-b border-gray-200 px-6">
              {allTabs.map((tab) => {
                const count = getTabCount(tab.id);
                const countLoading = isTabCountLoading(tab.id);
                return (
                  <div key={tab.id} className="flex flex-col">
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={() => setActiveTab(tab.id)}
                      className="whitespace-nowrap flex items-center gap-2"
                    >
                      {tab.name}
                      <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-medium bg-[#ECF3FE] rounded-full">
                        {countLoading ? (
                          <IconComponent
                            name="loader-icon"
                            width="1.5rem"
                            height="1.5rem"
                            className="w-3 h-3 animate-spin"
                          />
                        ) : count > 99 ? (
                          "99+"
                        ) : (
                          count
                        )}
                      </span>
                    </ButtonComponent>
                    <div
                      className={
                        activeTab === tab.id
                          ? "h-1 rounded-md bg-[linear-gradient(96deg,var(--primary-gradient-color1)_0%,var(--primary-gradient-color2)_110%)]"
                          : "h-1 bg-transparent"
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {activeTab === "allNotifications" && <AllNotification closeNotificationModal={closeNotificationModal} />}

          {activeTab === "takeAction" && (
            <div className="p-4 flex-1 overflow-y-auto">
              <ActionCards onCountChange={handleTakeActionCountChange} />
            </div>
          )}

          {activeTab === "exportedFiles" && (
            <div className="flex-1 overflow-y-auto">
              <ExportedFiles onCountChange={handleExportedFilesCountChange} />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Notification;
